import { useState, useCallback } from "react";
import type { File } from "@tellescope/types-models";
import { useEnduserStore } from "../store/hooks";

interface UseFileDownloadOptions {
  /** Called when download starts */
  onStart?: () => void;
  /** Called when download completes successfully */
  onSuccess?: () => void;
  /** Called when download fails */
  onError?: (error: Error) => void;
}

interface UseFileDownloadReturn {
  /** Trigger a file download */
  download: (file: File) => Promise<void>;
  /** Whether a download is currently in progress */
  isDownloading: boolean;
  /** Error from the last download attempt */
  error: Error | null;
}

/**
 * Hook for downloading files using the Tellescope SDK.
 *
 * Uses the `file_download_URL` API to get a presigned URL, then triggers
 * a browser download using that URL.
 *
 * @example
 * ```tsx
 * const { download, isDownloading } = useFileDownload();
 *
 * return (
 *   <Button onClick={() => download(file)} disabled={isDownloading}>
 *     {isDownloading ? 'Downloading...' : 'Download'}
 *   </Button>
 * );
 * ```
 */
export function useFileDownload(options: UseFileDownloadOptions = {}): UseFileDownloadReturn {
  const { onStart, onSuccess, onError } = options;
  const session = useEnduserStore((state) => state.session);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const download = useCallback(
    async (file: File) => {
      if (!session) {
        const err = new Error("No active session");
        setError(err);
        onError?.(err);
        return;
      }

      if (!file.secureName) {
        const err = new Error("File has no secureName - cannot download");
        setError(err);
        onError?.(err);
        return;
      }

      setIsDownloading(true);
      setError(null);
      onStart?.();

      try {
        // Step 1: Get the presigned download URL from the API
        const { downloadURL } = await session.api.files.file_download_URL({
          secureName: file.secureName,
        });

        // Step 2: Fetch the file content as a blob
        const response = await fetch(downloadURL);
        if (!response.ok) {
          throw new Error(`Download failed: ${response.status} ${response.statusText}`);
        }
        const blob = await response.blob();

        // Step 3: Create a blob URL and trigger browser download
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL after a short delay
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

        onSuccess?.();
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Download failed");
        setError(error);
        onError?.(error);
      } finally {
        setIsDownloading(false);
      }
    },
    [session, onStart, onSuccess, onError]
  );

  return {
    download,
    isDownloading,
    error,
  };
}
